import {
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import { IconEye, IconEyeOff } from "@tabler/icons-react";
import { useState } from "react";
import { styled } from "@mui/material/styles";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import { useLocation } from "react-router-dom";

const MainContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  minHeight: "100vh",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));

const LeftPanel = styled(Box)(({ theme }) => ({
  background: "linear-gradient(195deg,  #004030, #191919)",
  position: "relative",
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flex: 1,
  minHeight: "100vh",
  [theme.breakpoints.down("md")]: {
    minHeight: "40vh",
    flex: "none",
  },
  [theme.breakpoints.down("sm")]: {
    minHeight: "35vh",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    bottom: -200,
    left: -200,
    width: 400,
    height: 400,
    border: "1px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "50%",
    [theme.breakpoints.down("md")]: {
      bottom: -150,
      left: -150,
      width: 300,
      height: 300,
    },
    [theme.breakpoints.down("sm")]: {
      bottom: -100,
      left: -100,
      width: 200,
      height: 200,
    },
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: -100,
    left: -100,
    width: 250,
    height: 250,
    border: "1px solid rgba(255, 255, 255, 0.15)",
    borderRadius: "50%",
    [theme.breakpoints.down("md")]: {
      bottom: -75,
      left: -75,
      width: 150,
      height: 150,
    },
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
}));

const RightPanel = styled(Box)(({ theme }) => ({
  backgroundColor: "#f5f5f5",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flex: 1,
  minHeight: "100vh",
  padding: theme.spacing(3),
  [theme.breakpoints.down("md")]: {
    minHeight: "60vh",
    padding: theme.spacing(2),
  },
  [theme.breakpoints.down("sm")]: {
    minHeight: "65vh",
    padding: theme.spacing(1),
  },
}));

const BrandContent = styled(Box)(({ theme }) => ({
  position: "relative",
  zIndex: 10,
  color: "white",
  maxWidth: 400,
  padding: theme.spacing(0, 4),
  textAlign: "left",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(0, 3),
    maxWidth: "100%",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0, 2),
  },
}));

const LoginForm = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  width: "100%",
  maxWidth: 400,
  backgroundColor: "white",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
  borderRadius: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(3),
    margin: theme.spacing(0, 1),
    borderRadius: theme.spacing(1),
  },
}));

export default function VerifyOtp() {
  const [otp, setOtp] = useState(["", "", "", ""]);

  const location = useLocation();
  const email = location.state?.email || ""; // fallback if user navigates directly
  // console.log("email", email);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState({
    open: false,
    severity: "success",
    vertical: "top",
    horizontal: "right",
    message: "",
  });

  const navigate = useNavigate();
  const handleClose = () => setState({ ...state, open: false });
  const { vertical, horizontal, open, message, severity } = state;

  const formik = useFormik({
    initialValues: {
      email: email,
      otp: "", // will store combined OTP
    },
    validationSchema: Yup.object({
      otp: Yup.string().length(4, "OTP must be 4 digits").required("OTP is required"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const payload = {
          email: values.email,
          otp: values.otp, // this will be "1009" from the 4 input boxes
        };

        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/auth/verify-otp`,
          payload,
          { headers: { "Content-Type": "application/json" } }
        );

        if (response.data.status === 200) {
          setState({
            ...state,
            open: true,
            severity: "success",
            message: response.data.message,
          });

          setTimeout(() => {
            navigate("/forgot-password");
          }, 2000);
        }
      } catch (error) {
        setState({
          ...state,
          open: true,
          severity: "error",
          message: error.response?.data?.message || "Something went wrong",
        });
      } finally {
        setLoading(false);
      }
    },
  });

  const handleResendOtp = async () => {
    setLoading(true);
  
    // clear OTP state & Formik value
    setOtp(["", "", "", ""]);
    formik.setFieldValue("otp", "");
  
    try {
      const payload = { email };
  
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/auth/send-otp`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );
  
      if (response.data.status === 200) {
        setState({
          ...state,
          open: true,
          severity: "success",
          message: response.data.message,
        });
      }
    } catch (error) {
      console.error("Error:", error);
      setState({
        ...state,
        open: true,
        severity: "error",
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  };
  

  const handleOtpChange = (e, index) => {
    const { value } = e.target;
    const newOtp = [...otp];
  
    if (value.match(/^[0-9]$/)) {
      newOtp[index] = value;
      setOtp(newOtp);
  
      // update Formik field
      formik.setFieldValue("otp", newOtp.join(""));
  
      // move to next input automatically
      if (index < otp.length - 1) {
        document.getElementById(`otp-${index + 1}`).focus();
      }
    } else if (value === "") {
      newOtp[index] = "";
      setOtp(newOtp);
  
      // update Formik field
      formik.setFieldValue("otp", newOtp.join(""));
    }
  };
  
  
  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (otp[index] === "" && index > 0) {
        // move back to previous box if current is empty
        document.getElementById(`otp-${index - 1}`).focus();
      }
    }
  };
  
  

  return (
    <MainContainer>
      <Snackbar
        open={state.open}
        autoHideDuration={4000}
        onClose={handleClose}
        anchorOrigin={{ vertical, horizontal }}
      >
        <Alert onClose={handleClose} severity={state.severity} variant="filled">
          {state.message}
        </Alert>
      </Snackbar>
      <Backdrop open={loading} sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <LeftPanel>
        <BrandContent>
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: "bold",
              mb: { xs: 1, sm: 2 },
              fontSize: {
                xs: "2rem",
                sm: "2.5rem",
                md: "3rem",
                lg: "3.5rem",
              },
            }}
            style={{ color: "white" }}
          >
            CMGFP
          </Typography>
          <Typography
            variant="h6"
            sx={{
              mb: { xs: 2, sm: 3, md: 6 },
              lineHeight: 1.4,
              fontSize: {
                xs: "1rem",
                sm: "1.1rem",
                md: "1.25rem",
              },
              px: { xs: 1, sm: 0 },
            }}
            style={{ color: "white", fontWeight: "700", fontSize: "15px" }}
          >
            {"Chief Minister's Green Fellowship Programme"}
          </Typography>
        </BrandContent>
      </LeftPanel>

      <RightPanel>
        <Container
          sx={{ px: { xs: 1, sm: 3 } }}
          display="flex"
          // justifyContent="center"
          style={{ display: "flex", justifyContent: "center" }}
        >
          <LoginForm elevation={0}>
            <Box sx={{ textAlign: "center", mb: { xs: 3, sm: 4 } }}>
              <Typography
                variant="h4"
                component="h2"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  color: "text.primary",
                  fontSize: {
                    xs: "1.75rem",
                    sm: "2rem",
                    md: "2.125rem",
                  },
                }}
              >
                Verify OTP
              </Typography>
            </Box>

            <Box component="form" sx={{ mt: { xs: 2, sm: 3 } }} onSubmit={formik.handleSubmit}>
              <Box sx={{ display: "flex", gap: 1, justifyContent: "center", mb: 3 }}>
                {otp.map((value, index) => (
                 <TextField
                 key={index}
                 id={`otp-${index}`}
                 value={value}
                 onChange={(e) => handleOtpChange(e, index)}
                 onKeyDown={(e) => handleOtpKeyDown(e, index)}
                 inputProps={{
                   maxLength: 1,
                   style: { textAlign: "center", fontSize: "1.5rem" },
                 }}
                 sx={{ width: 60 }}
               />
               
                ))}
              </Box>

              {formik.errors.otp && formik.touched.otp && (
                <Typography color="error" sx={{ textAlign: "center", mb: 2 }}>
                  {formik.errors.otp}
                </Typography>
              )}

              {/* Buttons */}
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  style={{ color: "white" }}
                  sx={{
                    height: { xs: 48, sm: 56 },
                    backgroundColor: "#004030bf",
                    "&:hover": { backgroundColor: "#004030" },
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: { xs: "1rem", sm: "1.1rem" },
                    fontWeight: 500,
                  }}
                  disabled={loading}
                >
                  Verify OTP
                </Button>

                <Button
                  onClick={handleResendOtp}
                  fullWidth
                  variant="contained"
                  size="large"
                  style={{ color: "white" }}
                  sx={{
                    height: { xs: 48, sm: 56 },
                    backgroundColor: "#004030bf",
                    "&:hover": { backgroundColor: "#004030" },
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: { xs: "1rem", sm: "1.1rem" },
                    fontWeight: 500,
                  }}
                  disabled={loading}
                >

                  Resend OTP
                </Button>
              </Box>
            </Box>
          </LoginForm>
        </Container>
      </RightPanel>
    </MainContainer>
  );
}
